import { useLiveQuery } from 'dexie-react-hooks';
import { create } from 'zustand';
import type { Role, StaffMember } from '@sense/shared';
import { db } from './db';
import { useSession } from './session';

/**
 * The till is signed in once by an owner/manager (Firebase). After that, staff unlock it with a PIN.
 * The PIN lock turns on as soon as any active staff member has a PIN.
 * Note: this is a till lock enforced on the device; the server trusts the device session.
 */
interface CashierState {
  active: Pick<StaffMember, 'id' | 'name' | 'role'> | null;
  unlock(s: Pick<StaffMember, 'id' | 'name' | 'role'>): void;
  lock(): void;
}

export const useCashier = create<CashierState>((set) => ({
  active: null,
  unlock: (s) => set({ active: { id: s.id, name: s.name, role: s.role } }),
  lock: () => set({ active: null }),
}));

/** True when PIN unlock is in use on this till. */
export function usePinMode(): boolean | undefined {
  return useLiveQuery(async () => (await db.staff.filter((s) => s.active && !!s.pin_hash).count()) > 0, []);
}

/** Role used for what the UI allows: the unlocked staff member, else the signed-in account. */
export function useRole(): Role | undefined {
  const active = useCashier((s) => s.active);
  const role = useSession((s) => s.me?.user.role);
  return active?.role ?? role;
}

/** Who is billing right now (for the bill's cashier fields). */
export function currentCashier(): { id: string | null; name: string } {
  const a = useCashier.getState().active;
  const me = useSession.getState().me;
  return a ? { id: a.id, name: a.name } : { id: me?.user.id ?? null, name: me?.user.name ?? '' };
}

export function autoLockMinutes(): number {
  const v = Number(localStorage.getItem('sd.autolock') ?? '10');
  return Number.isFinite(v) ? v : 10;
}
